/**
 * Team Configuration Registry
 * Central export for all team configurations
 *
 * Environment Variable:
 * Set REACT_APP_TEAM_ID to override the default team at build time
 * Example: REACT_APP_TEAM_ID=slam npm run build
 */

import { slamConfig } from './slam';
import { slamballConfig } from './slamball';
import { slamgolfConfig } from './slamgolf';
import { slamsoccerConfig } from './slamsoccer';

// Legacy team configs removed - keeping only:
// - slam (baseball)
// - slamball (basketball)
// - slamgolf (golf)
// - slamsoccer (soccer)

/**
 * Supported sport codes - maps to backend systemPrompts SPORT_TO_USE_CASE
 */
export const SPORT_CODES = {
  // Baseball
  MLB: 'MLB',   // Major League Baseball
  CBB: 'CBB',   // College Baseball
  MiLB: 'MiLB', // Minor League Baseball
  NPB: 'NPB',   // Nippon Professional Baseball
  KBO: 'KBO',   // Korean Baseball Organization

  // Basketball
  NBA: 'NBA',   // NBA
  WNBA: 'WNBA', // WNBA
  MBB: 'MBB',   // Men's College Basketball
  WBB: 'WBB',   // Women's College Basketball
  EURO: 'EURO', // EuroLeague Basketball

  // Football
  PFB: 'PFB',   // Pro Football (NFL)
  CFB: 'CFB',   // College Football

  // Soccer
  FC: 'FC',     // Football Club (Soccer)

  // Hockey
  NHL: 'NHL',   // National Hockey League
  MiLH: 'MiLH', // Minor League Hockey

  // Formula 1
  F1: 'F1',     // Formula 1

  // Golf
  PGA: 'PGA',   // PGA Tour
};

/**
 * Sport categories for grouping
 */
export const SPORT_CATEGORIES = {
  baseball: ['MLB', 'CBB', 'MiLB', 'NPB', 'KBO'],
  basketball: ['NBA', 'WNBA', 'MBB', 'WBB', 'EURO'],
  football: ['PFB', 'CFB'],
  soccer: ['FC'],
  hockey: ['NHL', 'MiLH'],
  f1: ['F1'],
  golf: ['PGA'],
};

/**
 * Registry of all available team configurations
 * Only Baseball (slam) and Basketball (slamball) are supported
 */
export const teamRegistry = {
  // Baseball (MLB default)
  slam: slamConfig,

  // Basketball
  slamball: slamballConfig,

  // Golf
  slamgolf: slamgolfConfig,

  // Soccer
  slamsoccer: slamsoccerConfig,
};

/**
 * Get default team ID from environment or fallback
 * Priority: REACT_APP_TEAM_ID env var > 'slam' default
 */
function getDefaultTeamId() {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_TEAM_ID) {
    return process.env.REACT_APP_TEAM_ID;
  }
  return 'slam';
}

/**
 * Default team configuration
 */
export const defaultTeamId = getDefaultTeamId();
export const defaultConfig = teamRegistry[defaultTeamId] || slamConfig;

/**
 * Get team configuration by ID
 * @param {string} teamId - Team identifier
 * @returns {Object} Team configuration
 */
export function getTeamConfig(teamId) {
  const config = teamRegistry[teamId];
  if (!config) {
    console.warn(`Team config not found for "${teamId}", falling back to default`);
    return defaultConfig;
  }
  return config;
}

/**
 * Get list of available team IDs
 * @returns {string[]} Array of team IDs
 */
export function getAvailableTeams() {
  return Object.keys(teamRegistry);
}

/**
 * Check if a team config exists
 * @param {string} teamId - Team identifier
 * @returns {boolean}
 */
export function teamExists(teamId) {
  return teamId in teamRegistry;
}

/**
 * Get teams by sport code
 * @param {string} sportCode - Sport code (e.g., 'MLB', 'MBB', 'CFB')
 * @returns {string[]} Array of team IDs matching the sport code
 */
export function getTeamsBySportCode(sportCode) {
  return Object.entries(teamRegistry)
    .filter(([, config]) => config.sportCode === sportCode)
    .map(([teamId]) => teamId);
}

/**
 * Get teams by sport category
 * @param {string} category - Sport category ('baseball', 'basketball', 'football', 'other')
 * @returns {string[]} Array of team IDs in the category
 */
export function getTeamsBySportCategory(category) {
  const sportCodes = SPORT_CATEGORIES[category] || [];
  return Object.entries(teamRegistry)
    .filter(([, config]) => sportCodes.includes(config.sportCode))
    .map(([teamId]) => teamId);
}

/**
 * Get teams by sport (legacy support - uses category)
 * @returns {string[]} Array of baseball team IDs
 */
export function getBaseballTeams() {
  return getTeamsBySportCategory('baseball');
}

/**
 * Get basketball teams (legacy support - uses category)
 * @returns {string[]} Array of basketball team IDs
 */
export function getBasketballTeams() {
  return getTeamsBySportCategory('basketball');
}

/**
 * Get golf teams (uses category)
 * @returns {string[]} Array of golf team IDs
 */
export function getGolfTeams() {
  return getTeamsBySportCategory('golf');
}

/**
 * Get soccer teams (uses category)
 * @returns {string[]} Array of soccer team IDs
 */
export function getSoccerTeams() {
  return getTeamsBySportCategory('soccer');
}

/**
 * Get sport code for a team
 * @param {string} teamId - Team identifier
 * @returns {string|null} Sport code or null if not found
 */
export function getSportCodeForTeam(teamId) {
  const config = teamRegistry[teamId];
  return config?.sportCode || null;
}

/**
 * Get sport category for a sport code
 * @param {string} sportCode - Sport code (e.g., 'MLB', 'MBB')
 * @returns {string|null} Category ('baseball', 'basketball', etc.) or null
 */
export function getSportCategory(sportCode) {
  for (const [category, codes] of Object.entries(SPORT_CATEGORIES)) {
    if (codes.includes(sportCode)) {
      return category;
    }
  }
  return null;
}

// Re-export individual configs for direct import
export { slamConfig } from './slam';
export { slamballConfig } from './slamball';
export { slamgolfConfig } from './slamgolf';
export { slamsoccerConfig } from './slamsoccer';
